import React, { useEffect, useState } from 'react';
import config from '../config';
import { useNavigate, useLocation } from 'react-router-dom';

// Define a User type
interface User {
  email: string;
  name: string;
  picture?: string;
}

// AuthContext to manage auth state across the app
export const AuthContext = React.createContext<{
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
}>({
  isAuthenticated: false,
  user: null,
  loading: true,
  login: () => {},
  logout: () => {},
});

// Auth Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Check authentication status on mount and when location changes
  useEffect(() => {
    checkAuthStatus();
  }, [location.pathname]);

  // Function to check if user is authenticated
  const checkAuthStatus = async () => {
    try {
      console.log('Checking auth status...');
      const response = await fetch(`${config.apiBaseUrl}/index.php`, {
        method: 'GET',
        credentials: 'include', // Include cookies in the request
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Auth status response:', data);
      
      setIsAuthenticated(data.authenticated);
      
      // If user data is available, store it
      if (data.user) {
        setUser(data.user);
      } else if (data.authenticated) {
        // If authenticated but no user data, fetch user profile
        fetchUserProfile();
      }
      
      setLoading(false);
      
      // If not authenticated and not already on login page, redirect to login
      if (!data.authenticated && location.pathname !== '/login') {
        console.log('Not authenticated, redirecting to login');
        navigate('/login');
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      
      // On error, redirect to login if not already there
      if (location.pathname !== '/login') {
        navigate('/login');
      }
    }
  };
  
  // Function to fetch user profile data
  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/auth/profile.php`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setUser(data.user);
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  // Login function - redirects to Google OAuth
  const login = async () => {
    try {
      console.log('Starting login process...');
      const response = await fetch(`${config.apiBaseUrl}/auth/login.php`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Login API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Login response:', data);
      
      if (data.success && data.auth_url) {
        // Redirect to Google login
        window.location.href = data.auth_url;
      } else {
        throw new Error('No auth URL returned from login endpoint');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
    }
  };

  // Logout function
  const logout = async () => {
    try {
      console.log('Logging out...');
      await fetch(`${config.apiBaseUrl}/auth/logout.php`, {
        method: 'GET',
        credentials: 'include', // Include cookies in the request
        headers: {
          'Accept': 'application/json'
        }
      });
      
      setIsAuthenticated(false);
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear the local state
      setIsAuthenticated(false);
      setUser(null);
      navigate('/login');
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth component that checks for authentication
const Auth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = React.useContext(AuthContext);
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);
  
  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }
  
  return isAuthenticated ? <>{children}</> : null;
};

export default Auth; 